import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authAPI } from '../services/api';
import { loggingService } from '../services/loggingService';

interface AuthContextType {
  	user: User | null;
  	token: string | null;
  	login: (username: string, password: string) => Promise<boolean>;
  	logout: () => void;
  	isAuthenticated: boolean;
  	loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  	const context = useContext(AuthContext);
  	if(context === undefined){
    	throw new Error('useAuth must be used within an AuthProvider');
  	}
  	return context;
};

interface AuthProviderProps {
  	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  	const [user, setUser] = useState<User | null>(null);
  	const [token, setToken] = useState<string | null>(null);
  	const [loading, setLoading] = useState(true);

  	useEffect(() => {
    	const storedToken = localStorage.getItem('token');
    	const storedUser = localStorage.getItem('user');
    	if(storedToken && storedUser){
      		setToken(storedToken);
      		setUser(JSON.parse(storedUser));
      		authAPI.checkAuth().then(() => {
          		setLoading(false);
        	}).catch(() => {
          		// Token geçersizse temizle
          		localStorage.removeItem('token');
          		localStorage.removeItem('user');
          		setToken(null);
          		setUser(null);
          		setLoading(false);
        	});
    	}else{
      		setLoading(false);
    	}
  	}, []);

  	const login = async(username: string, password: string): Promise<boolean> => {
		try{
			const response = await authAPI.login({username, password});
			const data: AuthResponse = response.data;
			if(data.status === 'success'){
				const {token: newToken, user_id, username: userUsername} = data.data;
				const userData: User = {id: user_id, username: userUsername};
				setToken(newToken);
				setUser(userData);
				localStorage.setItem('token', newToken);
				localStorage.setItem('user', JSON.stringify(userData));
				return true;
			}
			return false;
		}catch(error){
			console.error('Login error:', error);
			return false;
		}
  	};

  	const logout = () => {
    	loggingService.logLogout();
    	setToken(null);
    	setUser(null);
    	localStorage.removeItem('token');
    	localStorage.removeItem('user');
	};

  	const value: AuthContextType = { user, token, login, logout, isAuthenticated: !!token && !!user, loading };

  	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
